import styled, { css } from 'styled-components';
import { BaSeTheme } from '../../theme';
import { ThemeColorValue } from '../../theme/theme-interface';
import { ColorMapper } from '../../utils/color-transformation/color-mapper';
import { numberWithUnit } from '../../utils/string-utils';

export interface InputRangeProps {
  color: ThemeColorValue;
  width: string | number;
  height: string | number;
  orientation: 'vertical' | 'horizontal';
  $percentage: number;
  $startPercentage?: number;
  $isRange?: boolean;
  $hasExternalTrack?: boolean;
}

export interface SliderTrackProps
  extends Pick<InputRangeProps, 'color' | 'orientation' | '$percentage'>,
    Required<Pick<InputRangeProps, '$startPercentage' | '$isRange'>> {
  $isDisabled: boolean;
}

export interface SliderContainerProps
  extends Pick<InputRangeProps, 'width' | 'height' | 'orientation'> {}

export interface SliderControlProps
  extends Pick<InputRangeProps, 'height' | 'orientation'> {}

export interface ValueLabelProps
  extends Pick<InputRangeProps, '$percentage' | 'orientation'> {}

export interface ValueTooltipProps extends ValueLabelProps {}

export interface LimitsProps extends Pick<InputRangeProps, 'orientation'> {
  $alignment: 'start' | 'both' | 'end';
}

const thumbPosition = (percentage: number) =>
  `calc(${percentage}% + ${(0.5 - percentage / 100) * 1.5}em)`;

const tooltipPosition = (percentage: number) =>
  `calc(${percentage}% + ${(0.5 - percentage / 100) * 1.5 + 0.05}em)`;

export const InputRange = styled.input.attrs({
  type: 'range',
})<InputRangeProps>`
  --thumb-height: 1.4em;
  --control-height: 3em;
  --track-height: 0.25em;
  --track-color: ${BaSeTheme.colors.institucionais.cinzaSebrae90};

  position: relative;
  background: #fff0;
  overflow: visible;
  outline: none;
  height: var(--control-height);
  margin: 0;
  padding: 0;
  border: 0;
  box-sizing: border-box;
  font: inherit;

  ${({
    color,
    width,
    height,
    orientation,
    $percentage,
    $startPercentage = 0,
    $isRange,
  }) => css`
    color: ${color};
    width: ${orientation === 'vertical'
      ? 'var(--control-height)'
      : $isRange
        ? '100%'
        : numberWithUnit(width)};
    height: ${orientation === 'vertical'
      ? numberWithUnit(height)
      : 'var(--control-height)'};
    ${$isRange &&
    css`
      position: absolute;
      inset: 0;
      pointer-events: none;
    `}
    --range-start: ${$startPercentage}%;
    --range-progress: ${$percentage}%;
    --thumb-color: ${color};
    --thumb-hover-shadow: ${ColorMapper.createTransparent(color, 60, 14)};
    --thumb-focus-shadow: ${ColorMapper.createTransparent(color, 60, 22)};
    --thumb-pressed-shadow: ${ColorMapper.createTransparent(color, 60, 32)};
  `}

  ${({ orientation }) =>
    orientation === 'vertical' &&
    css`
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      writing-mode: vertical-lr;
      direction: rtl;
      cursor: row-resize;

      &::-webkit-slider-thumb {
        margin-top: 0;
      }
    `};

  &:active {
    cursor: grabbing;
  }

  & {
    -webkit-appearance: none;
    appearance: none;
  }

  &::-webkit-slider-runnable-track,
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
  }

  &::-webkit-slider-thumb {
    width: var(--thumb-height);
    height: var(--thumb-height);
    margin-top: calc((var(--track-height) - var(--thumb-height)) / 2);
    background-color: var(--thumb-color);
    border: 0;
    border-radius: 50%;
    box-shadow: none;
    transition:
      background-color ease 100ms,
      box-shadow ease 100ms;
    pointer-events: auto;
  }

  &::-webkit-slider-thumb:hover {
    cursor: grab;
    box-shadow: 0 0 0 8px var(--thumb-hover-shadow);
  }

  &:focus::-webkit-slider-thumb {
    box-shadow: 0 0 0 9px var(--thumb-focus-shadow);
  }

  &::-webkit-slider-thumb:active {
    box-shadow: 0 0 0 10px var(--thumb-pressed-shadow);
    cursor: grabbing;
  }

  &::-webkit-slider-runnable-track {
    height: var(--track-height);
    background: ${({ $hasExternalTrack }) =>
      $hasExternalTrack
        ? 'transparent'
        : `linear-gradient(
            to right,
            var(--track-color) 0 var(--range-start),
            currentColor var(--range-start) var(--range-progress),
            var(--track-color) var(--range-progress) 100%
          )`};
    border-radius: var(--track-height);
  }

  &:disabled::-webkit-slider-thumb {
    background-color: ${BaSeTheme.colors.institucionais.cinzaSebrae45};
    box-shadow: none;
    cursor: not-allowed;
  }

  &::-moz-range-track,
  &::-moz-range-thumb {
    appearance: none;
  }

  &::-moz-range-thumb {
    background: var(--thumb-color);
    border: 0;
    width: var(--thumb-height);
    height: var(--thumb-height);
    border-radius: 50%;
    box-shadow: none;
    cursor: grab;
    transition:
      background-color ease 100ms,
      box-shadow ease 100ms;
    pointer-events: auto;
  }

  &::-moz-range-track {
    width: 100%;
    background: ${({ $hasExternalTrack }) =>
      $hasExternalTrack
        ? 'transparent'
        : `linear-gradient(
            to right,
            var(--track-color) 0 var(--range-start),
            currentColor var(--range-start) var(--range-progress),
            var(--track-color) var(--range-progress) 100%
          )`};
  }

  &::-moz-range-progress {
    appearance: none;
    background: transparent;
  }

  &::-moz-range-track,
  &::-moz-range-progress {
    height: calc(var(--track-height) + 1px);
    border-radius: var(--track-height);
  }

  &::-moz-range-thumb:hover {
    box-shadow: 0 0 0 8px var(--thumb-hover-shadow);
  }

  &:focus::-moz-range-thumb {
    box-shadow: 0 0 0 9px var(--thumb-focus-shadow);
  }

  &::-moz-range-thumb:active {
    box-shadow: 0 0 0 10px var(--thumb-pressed-shadow);
    cursor: grabbing;
  }

  &:disabled::-moz-range-thumb {
    background-color: ${BaSeTheme.colors.institucionais.cinzaSebrae45};
    box-shadow: none;
    cursor: not-allowed;
  }
`;

export const SliderContainer = styled.div<SliderContainerProps>`
  position: relative;
  width: ${({ width }) => numberWithUnit(width)};
  ${({ height, orientation }) =>
    orientation === 'vertical' &&
    css`
      height: ${numberWithUnit(height)};
    `}
`;

export const SliderLabel = styled.div`
  margin-bottom: 1.6em;
`;

export const SliderControl = styled.div<SliderControlProps>`
  position: relative;
  height: ${({ height, orientation }) =>
    orientation === 'vertical' ? numberWithUnit(height) : '3em'};
`;

export const SliderTrack = styled.div<SliderTrackProps>`
  position: absolute;
  border-radius: 0.25em;
  ${({ orientation }) =>
    orientation === 'horizontal'
      ? css`
          top: 50%;
          right: 0;
          left: 0;
          height: 0.25em;
          transform: translateY(-50%);
        `
      : css`
          top: 0;
          bottom: 0;
          left: 50%;
          width: 0.25em;
          transform: translateX(-50%);
        `}
  background: ${({
    color,
    $startPercentage,
    $percentage,
    $isRange,
    $isDisabled,
    orientation,
  }) => {
    const startPosition = $isRange ? thumbPosition($startPercentage) : '0';
    const progressColor = $isDisabled
      ? BaSeTheme.colors.institucionais.cinzaSebrae75
      : color;
    return `linear-gradient(
      to ${orientation === 'horizontal' ? 'right' : 'top'},
      ${BaSeTheme.colors.institucionais.cinzaSebrae90} 0
        ${startPosition},
      ${progressColor} ${startPosition} ${thumbPosition($percentage)},
      ${BaSeTheme.colors.institucionais.cinzaSebrae90}
        ${thumbPosition($percentage)} 100%
    )`;
  }};
`;

export const ValueTooltip = styled.output<ValueTooltipProps>`
  position: absolute;
  ${({ $percentage, orientation }) =>
    orientation === 'horizontal'
      ? css`
          left: ${tooltipPosition($percentage)};
          bottom: calc(50% + 1.25em);
          transform: translateX(-50%);
        `
      : css`
          bottom: ${thumbPosition($percentage)};
          left: calc(50% + 1.25em);
          transform: translateY(50%);
        `}
  padding: 0.5em 0.625em;
  border-radius: 0.25em;
  background: ${BaSeTheme.colors.institucionais.azulSebrae20};
  white-space: nowrap;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    border: 0.375em solid transparent;
    ${({ orientation }) =>
      orientation === 'horizontal'
        ? css`
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            border-top-color: ${BaSeTheme.colors.institucionais.azulSebrae20};
          `
        : css`
            top: 50%;
            right: 100%;
            transform: translateY(-50%);
            border-right-color: ${BaSeTheme.colors.institucionais.azulSebrae20};
          `}
  }
`;

export const ValueLabel = styled.output<ValueLabelProps>`
  position: absolute;
  ${({ $percentage, orientation }) =>
    orientation === 'horizontal'
      ? css`
          left: ${thumbPosition($percentage)};
          bottom: calc(50% + 1.1em);
          transform: translateX(-50%);
        `
      : css`
          bottom: ${thumbPosition($percentage)};
          left: calc(50% + 1.3em);
          transform: translateY(50%);
        `}
  white-space: nowrap;
  pointer-events: none;
`;

export const Limits = styled.div<LimitsProps>`
  display: flex;
  ${({ $alignment, orientation }) =>
    orientation === 'horizontal'
      ? css`
          justify-content: ${$alignment === 'both'
            ? 'space-between'
            : $alignment === 'end'
              ? 'flex-end'
              : 'flex-start'};
          margin-top: -0.25em;
        `
      : css`
          position: absolute;
          inset: 0 auto 0 0;
          flex-direction: column-reverse;
          justify-content: ${$alignment === 'both'
            ? 'space-between'
            : $alignment === 'end'
              ? 'flex-end'
              : 'flex-start'};
          pointer-events: none;
          margin-left: 0.25em;
        `}
`;
